import { useState } from "react";
import { archiveEvents, archiveFilters } from "@/mocks/homeData";
import type { ArchiveEvent } from "@/mocks/homeData";
import SectionHeading from "./SectionHeading";

const statusStyles: Record<string, string> = {
  追踪中: "bg-primary-100 text-primary-700",
  已核实: "bg-accent-100 text-accent-800",
  待确认: "bg-secondary-100 text-secondary-800",
  已失效: "bg-background-200 text-foreground-600",
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${statusStyles[status] ?? statusStyles["已失效"]}`}>
      {status}
    </span>
  );
}

function MetaRow({ item }: { item: ArchiveEvent }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-foreground-500">
      <span className="flex items-center gap-1">
        <i className="ri-broadcast-line"></i>
        {item.platform}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-time-line"></i>
        {item.firstSeen}
      </span>
      <span className="flex items-center gap-1 text-primary-600">
        <i className="ri-fire-line"></i>
        {item.heat}
      </span>
      <span className="flex items-center gap-1">
        <i className="ri-chat-3-line"></i>
        {item.comments}
      </span>
    </div>
  );
}

function LargeCard({ item }: { item: ArchiveEvent }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300 md:col-span-2 md:flex-row">
      {item.image ? (
        <div className="h-52 w-full overflow-hidden md:h-auto md:w-2/5">
          <img
            src={item.image}
            alt={`${item.code} ${item.title}`}
            title={`${item.title} · 黑料照片首发出处`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          <span className="zone-label rounded bg-foreground-950 px-2 py-0.5 text-[11px] text-background-50">
            {item.code}
          </span>
          <StatusBadge status={item.status} />
          <span className="text-[11px] text-foreground-500">{item.category}</span>
        </div>
        <h4 className="mt-3 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-xl">
          <a href="#archive">{item.title}</a>
        </h4>
        <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-foreground-600 md:text-sm">{item.summary}</p>
        <div className="mt-auto">
          <MetaRow item={item} />
        </div>
      </div>
    </article>
  );
}

function SmallCard({ item }: { item: ArchiveEvent }) {
  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-secondary-300">
      {item.image ? (
        <div className="h-32 w-full overflow-hidden">
          <img
            src={item.image}
            alt={`${item.code} ${item.title}`}
            title={`${item.title} · 黑料照片泄露源头`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="zone-label rounded bg-background-200 px-1.5 py-0.5 text-[10px] text-foreground-700">
            {item.code}
          </span>
          <StatusBadge status={item.status} />
        </div>
        <h4 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          <a href="#archive">{item.title}</a>
        </h4>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">{item.summary}</p>
        <div className="mt-auto">
          <MetaRow item={item} />
        </div>
      </div>
    </article>
  );
}

export default function ArchiveSection() {
  const [active, setActive] = useState(archiveFilters[0]);

  return (
    <div>
      <SectionHeading
        index="01"
        label="事件归档"
        title="按事件编号归档的出处库"
        desc="大小卡混排：每条归档都标注首发出处、最早出现时间与当前核实状态。"
      />

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {archiveFilters.map((filter) => {
          const isActive = active === filter;
          return (
            <button
              key={filter}
              type="button"
              onClick={() => setActive(filter)}
              className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                isActive
                  ? "border-primary-500 bg-primary-500 text-white"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-secondary-300 hover:bg-secondary-100"
              }`}
            >
              {filter}
            </button>
          );
        })}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {archiveEvents.map((item) =>
          item.size === "large" ? (
            <LargeCard key={item.id} item={item} />
          ) : (
            <SmallCard key={item.id} item={item} />
          ),
        )}
      </div>
    </div>
  );
}